import React, { useEffect, useState } from 'react';
import { Switch, Route, Link, useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useInstitucion } from '../../context/InstitucionContext';
import { getInstitucionAdmin, setInstitucionAdmin } from '../../services/authService';
import { getInstitucionesAdmin } from '../../services/institucionesService';
import {
  esSuperAdmin,
  puedeGestionarCategorias,
  puedeGestionarUsuarios,
  puedeEditarBranding,
  puedeGestionarInstituciones,
  ROLES,
} from '../../utils/permisos';
import PrivateRoute from '../../components/PrivateRoute';
import { PantallaCarga } from '../../components/PantallaCarga';
import MarcaPusah from '../../components/MarcaPusah';
import AdminPublicaciones from './AdminPublicaciones';
import AdminPublicacionForm from './AdminPublicacionForm';
import AdminCategorias from './AdminCategorias';
import AdminCategoriaForm from './AdminCategoriaForm';
import AdminMiInstitucion from './AdminMiInstitucion';
import AdminUsuarios from './AdminUsuarios';
import AdminUsuarioForm from './AdminUsuarioForm';
import AdminInstituciones from './AdminInstituciones';
import AdminInstitucionForm from './AdminInstitucionForm';

const ROLES_ADMIN = [ROLES.SUPER_ADMIN, ROLES.ADMIN_INSTITUCION];
const ROLES_SUPER = [ROLES.SUPER_ADMIN];

// Pantallas que operan sobre una institución concreta: un SuperAdmin debe elegir una primero.
const RequiereInstitucion = ({ children }) => {
  const { institucion } = useInstitucion();
  if (institucion) return children;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
      <h2 className="text-lg font-bold text-gray-900 mb-2">Selecciona una institución</h2>
      <p className="text-gray-500 text-sm">
        Elige la institución activa en la barra superior para gestionar sus publicaciones, categorías y marca.
      </p>
    </div>
  );
};

const AdminLayout = () => {
  const { usuario, logout, refrescar } = useAuth();
  const { institucion, paleta: colors, nombreMarca, modo, seleccionar, ruta } = useInstitucion();
  const history = useHistory();
  const location = useLocation();
  const superAdmin = esSuperAdmin(usuario);
  const [instituciones, setInstituciones] = useState([]);

  // Rol, institución y alcance vigentes (pueden haber cambiado desde el login).
  useEffect(() => {
    refrescar();
  }, [refrescar]);

  // Lista para el selector del SuperAdmin.
  useEffect(() => {
    if (!superAdmin) return;
    getInstitucionesAdmin()
      .then((lista) => setInstituciones(lista.filter((i) => i.activo)))
      .catch(() => setInstituciones([]));
  }, [superAdmin]);

  // Institución que debe estar activa en el panel:
  //  - usuarios con institución: la suya (en modo plataforma la ruta /admin no la lleva; en modo
  //    dominio la fija el host y el login ya impidió entrar con un usuario de otra);
  //  - SuperAdmin: la última elegida (localStorage), si hay.
  const slugDeseado = superAdmin
    ? getInstitucionAdmin()
    : usuario && usuario.institucion ? usuario.institucion.slug : null;
  const esperandoInstitucion =
    Boolean(slugDeseado) &&
    (!institucion || institucion.slug !== slugDeseado) &&
    (modo === 'plataforma' || superAdmin);

  useEffect(() => {
    if (esperandoInstitucion) seleccionar(slugDeseado);
  }, [esperandoInstitucion, slugDeseado, seleccionar]);

  const handleLogout = () => {
    logout();
    seleccionar(null);
    history.replace('/admin/login');
  };

  const handleSeleccionInstitucion = (e) => {
    const slug = e.target.value || null;
    setInstitucionAdmin(slug);
    seleccionar(slug);
    history.push('/admin');
  };

  const seccion = location.pathname.startsWith('/admin/categorias')
    ? 'categorias'
    : location.pathname.startsWith('/admin/usuarios')
      ? 'usuarios'
      : location.pathname.startsWith('/admin/instituciones')
        ? 'instituciones'
        : location.pathname.startsWith('/admin/institucion')
          ? 'institucion'
          : 'publicaciones';

  const tabClass = (activa) =>
    `inline-block px-1 pb-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
      activa ? 'text-gray-900' : 'text-gray-500 border-transparent hover:text-gray-700'
    }`;
  const tabStyle = (activa) => (activa ? { borderBottomColor: colors.primary } : undefined);

  // No montar las pantallas hasta tener la institución correcta: sus peticiones ya deben salir
  // con el X-Institucion adecuado y no queremos que se desmonten a mitad de carga.
  if (esperandoInstitucion) return <PantallaCarga mensaje="Cargando institución…" />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Barra superior */}
      <header
        className="shadow-md"
        style={{
          background: `linear-gradient(135deg, ${colors.headerGradientStart}, ${colors.headerGradientMid}, ${colors.headerGradientEnd})`,
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/admin" className="flex items-center gap-3 min-w-0">
            {institucion && institucion.logoUrl && (
              <img
                src={`${process.env.PUBLIC_URL}${institucion.logoUrl}`}
                alt={`Logo ${institucion.nombreCorto}`}
                className="h-9"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            {/* Marca de la plataforma junto al logo de la institución */}
            <span className="hidden md:inline-flex items-center gap-3">
              {institucion && institucion.logoUrl && <span className="h-7 w-px bg-white/30" aria-hidden="true" />}
              <MarcaPusah variante="blanco" className="h-6" conEnlace={false} />
            </span>
            <span className="text-white font-bold text-lg hidden sm:block truncate">
              Administración · {institucion ? institucion.nombreCorto : nombreMarca}
            </span>
          </Link>

          <div className="flex items-center gap-3">
            {superAdmin && (
              <select
                value={institucion ? institucion.slug : ''}
                onChange={handleSeleccionInstitucion}
                className="text-sm rounded-lg px-2.5 py-1.5 bg-white/15 text-white border border-white/20 focus:outline-none"
                title="Institución activa"
              >
                <option value="" className="text-gray-900">— Institución activa —</option>
                {instituciones.map((i) => (
                  <option key={i.id} value={i.slug} className="text-gray-900">{i.nombreCorto}</option>
                ))}
              </select>
            )}
            {institucion && (
              <Link
                to={ruta('/')}
                className="text-white/85 text-sm hidden md:block hover:text-white"
                title="Ver el sitio público"
              >
                Ver sitio
              </Link>
            )}
            <span className="text-white/85 text-sm hidden lg:block">
              {usuario?.nombre || usuario?.email}
            </span>
            <button
              onClick={handleLogout}
              className="text-sm font-medium text-white bg-white/15 hover:bg-white/25 border border-white/20 rounded-lg px-3 py-1.5 transition-colors"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      {/* Pestañas de navegación (según rol) */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-3 flex gap-6 overflow-x-auto">
          <Link to="/admin" className={tabClass(seccion === 'publicaciones')} style={tabStyle(seccion === 'publicaciones')}>
            Publicaciones
          </Link>
          {puedeGestionarCategorias(usuario) && (
            <Link to="/admin/categorias" className={tabClass(seccion === 'categorias')} style={tabStyle(seccion === 'categorias')}>
              Categorías
            </Link>
          )}
          {puedeGestionarUsuarios(usuario) && (
            <Link to="/admin/usuarios" className={tabClass(seccion === 'usuarios')} style={tabStyle(seccion === 'usuarios')}>
              Usuarios
            </Link>
          )}
          {puedeEditarBranding(usuario) && (
            <Link to="/admin/institucion" className={tabClass(seccion === 'institucion')} style={tabStyle(seccion === 'institucion')}>
              Mi institución
            </Link>
          )}
          {puedeGestionarInstituciones(usuario) && (
            <Link to="/admin/instituciones" className={tabClass(seccion === 'instituciones')} style={tabStyle(seccion === 'instituciones')}>
              Instituciones
            </Link>
          )}
        </div>
      </nav>

      {/* Contenido */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Switch>
          <Route exact path="/admin">
            <RequiereInstitucion><AdminPublicaciones /></RequiereInstitucion>
          </Route>
          <Route exact path="/admin/publicaciones/nueva">
            <RequiereInstitucion><AdminPublicacionForm /></RequiereInstitucion>
          </Route>
          <Route exact path="/admin/publicaciones/:id/editar">
            <RequiereInstitucion><AdminPublicacionForm /></RequiereInstitucion>
          </Route>

          <PrivateRoute exact path="/admin/categorias" roles={ROLES_ADMIN}>
            <RequiereInstitucion><AdminCategorias /></RequiereInstitucion>
          </PrivateRoute>
          <PrivateRoute exact path="/admin/categorias/nueva" roles={ROLES_ADMIN}>
            <RequiereInstitucion><AdminCategoriaForm /></RequiereInstitucion>
          </PrivateRoute>
          <PrivateRoute exact path="/admin/categorias/:id/editar" roles={ROLES_ADMIN}>
            <RequiereInstitucion><AdminCategoriaForm /></RequiereInstitucion>
          </PrivateRoute>

          <PrivateRoute exact path="/admin/usuarios" roles={ROLES_ADMIN} component={AdminUsuarios} />
          <PrivateRoute exact path="/admin/usuarios/nuevo" roles={ROLES_ADMIN} component={AdminUsuarioForm} />
          <PrivateRoute exact path="/admin/usuarios/:id/editar" roles={ROLES_ADMIN} component={AdminUsuarioForm} />

          <PrivateRoute exact path="/admin/institucion" roles={ROLES_ADMIN}>
            <RequiereInstitucion><AdminMiInstitucion /></RequiereInstitucion>
          </PrivateRoute>

          <PrivateRoute exact path="/admin/instituciones" roles={ROLES_SUPER} component={AdminInstituciones} />
          <PrivateRoute exact path="/admin/instituciones/nueva" roles={ROLES_SUPER} component={AdminInstitucionForm} />
          <PrivateRoute exact path="/admin/instituciones/:id/editar" roles={ROLES_SUPER} component={AdminInstitucionForm} />
        </Switch>
      </main>
    </div>
  );
};

export default AdminLayout;
